import { Form, Formik } from "formik";
import { BiAddToQueue, BiSave } from "react-icons/bi";
import { AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { CreateDropdownInputGroup, FieldType, TranslationKey, Option } from "@typedefs/modulesInterfaces";
import { FastProject } from "@typedefs/interfaces";
import { useContextAwareGetAllTags, useContextAwareGetTagsByProject } from "@hooks/index";
import Button, { ButtonProps } from "@components/button/Button";
import TranslatableText from "@components/translations/TranslatableText";
import Modal from "@components/modal/Modal";
import FadeAnimation from "@components/animations/FadeAnimation";
import Skeleton from "react-loading-skeleton";
import Badge from "./Badge";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import ErrorModal from "@components/modal/ErrorModal";

interface EditTagsModalProps {
  modalOpen: boolean;
  handleSubmit: (initialTags: Option[], newTags: Option[]) => void;
  handleCancel: () => void;
  headerText?: TranslationKey;
  projectToEditTags: FastProject | null;
  isLoading?: boolean;
  error?: string | null;
  onClearError?: () => void;
}

const EditTagsModal = ({
  modalOpen,
  handleCancel,
  headerText,
  projectToEditTags,
  handleSubmit: applyChanges,
  isLoading,
  error,
  onClearError
}: EditTagsModalProps) => {
  const [tagList, setTagList] = useState<Option[]>([]);
  const { data: projectTags, isLoading: isLoadingTags } = useContextAwareGetTagsByProject(projectToEditTags?.id ?? 0, { skip: !projectToEditTags?.id, refetchOnMountOrArgChange: true });
  const { data: allTags } = useContextAwareGetAllTags();

  useEffect(() => {
    if (projectTags) setTagList(projectTags);
  }, [projectTags]);

  const handleRemoveTag = (tagId: number) =>
    setTagList(tagList.filter(tag => tag.id !== tagId));

  const handleAddTag = ({ new_tag }: { new_tag: string }) => {
    const selectedTagId = +new_tag;
    const tag = allTags?.find(tag => tag.id === selectedTagId);
    if (tag) setTagList([...tagList, tag]);
  };

  const handleCreateTag = ({ newTagName }: { newTagName: string }) => {
    const newTag = { id: Math.random(), name: newTagName };
    if (newTag) setTagList([...tagList, newTag]);
  };

  const applyTagsButton: ButtonProps = useMemo(() => {
    return {
      children: <div className="d-flex gap-8 text-white align-items-center">
        <TranslatableText className="fs-14 ff-medium" translationKey="main.apply_tags" />
        <BiSave size={20} color="var(--white)" />
      </div>,
      onClick: () => applyChanges(projectTags ?? [], tagList),
      disabled: isLoadingTags || isLoading,
      type: "button",
      classes: "b-quat-de br-6 p-1 bg-quat-br text-white"
    }
  }, [applyChanges, tagList, projectTags, isLoadingTags, isLoading]);

  const addTagInputGroup: CreateDropdownInputGroup = useMemo(() => {
    const tagOptions = allTags?.filter(tag => !tagList.some(t => t.id === tag.id));
    return {
      type: FieldType.CREATEABLE_SELECT,
      dropdownOptions: tagOptions ?? [],
      onCreate: (value: string) => handleCreateTag({ newTagName: value }),
      label: "project.add_new_tag",
      inputName: "new_tag",
    }
  }, [tagList, allTags]);

  return (
    <AnimatePresence mode="wait" onExitComplete={() => null}>
      {modalOpen && (
        <>
          <Modal
            headerColor="var(--primary-dark)"
            footerButtons={[applyTagsButton]}
            isHeaderDark
            headerText={headerText ?? "project.edit_tags"}
            handleClose={handleCancel}
            size="lg"
          >
            <div className="d-flex f-column gap-8 p-3">
              <span className="d-flex gap-8 fs-20 ff-medium-ext">
                {projectToEditTags?.name}
              </span>

              <div className="pt-1 d-flex f-column gap-8 justify-content-center">
                <TranslatableText className="fs-14 text-gray" translationKey="project.select_tag_or_create" />
                <Formik initialValues={{ new_tag: "" }} onSubmit={handleAddTag}>
                  <Form className="d-flex gap-8" style={{ width: "clamp(200px, 100%, 400px)" }}>
                    <GenericInputGroup inputGroup={addTagInputGroup} showCommentIcon={false} />
                    <Button type="submit" className="br-8 bg-prim-de px-1" disabled={isLoading}>
                      <BiAddToQueue size={20} color="var(--white)" />
                    </Button>
                  </Form>
                </Formik>
              </div>

              <div className="badge-container">
                <span><TranslatableText className="fs-14 text-gray" translationKey="project.current_tags" /> </span>
                {
                  tagList && tagList?.length === 0 ? (
                    <TranslatableText translationKey="project.no_tags" className="fs-14" />
                  ) :
                    <div className="masonry-columns" style={{ columnCount: 5 }}>
                      {isLoadingTags ? Array(3).fill(0).map((_, i) => (
                        <FadeAnimation key={`cart-skeleton-${i}`} delay={i * 0.1} isShown>
                          <Skeleton height={108} borderRadius={8} />
                        </FadeAnimation>
                      )) : tagList?.map((tag, i) => (
                        <FadeAnimation key={tag.id} delay={i * 0.1} isShown>
                          <Badge
                            text={tag.name}
                            color="var(--primary-deep)"
                            hoverColor="var(--primary-bright)"
                            onRemove={() => handleRemoveTag(tag.id)}
                            disabled={isLoading}
                          />
                        </FadeAnimation>
                      ))}
                    </div>
                }
              </div>
            </div>
          </Modal>

          <ErrorModal
            isOpen={!!error}
            errorMessage={error || ""}
            handleClose={onClearError}
          />
        </>
      )}
    </AnimatePresence>
  );
}

export default EditTagsModal;
